import React, { useCallback, useEffect, useState } from "react";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import {
  ToastSuccess,
  ToastError,
  Toggle,
  Spinner,
  Link,
  classes,
} from "@bloomberg/media-ui-core";
import {
  ChevronUp,
  ChevronDown,
  NotificationAdd,
} from "@bloomberg/media-ui-icons";
import { useRegUIClient } from "@foundation/reg-ui-client";
import { type UserPreferencesResponse } from "@foundation/watchlist-client";
import { DropdownMenu } from "../DropdownMenu";
import styles from "./styles.module.css";
import {
  fetchUserPreferences,
  updateUserPreferences,
} from "~api/portfolio/portfolioData";
import { LOADING_STATE } from "~constants/Portfolio";

const AUTO_HIDE_DURATION = 4000;
const WATCHLIST_ALERTS_HELP_PAGE_URL =
  "https://www.bloomberg.com/help/question/how-do-i-get-alerts-about-my-watchlist/";

const OPT_IN_TOAST_MSG = "You’ve opted into significant price change alerts.";
const OPT_OUT_TOAST_MSG =
  "You’ve opted out of significant price change alerts.";
const ERROR_TOAST_MSG = "Error updating alerts preference.";

export default function WatchlistAlertsDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [isAlertsEmailsEnabled, setIsAlertsEmailsEnabled] = useState(false);
  const [isSuccessToastOpen, setIsSuccessToastOpen] = useState(false);
  const [isErrorToastOpen, setIsErrorToastOpen] = useState(false);
  const [loadingState, setLoadingState] = useState(LOADING_STATE.LOADING);
  const { session } = useRegUIClient();
  const bconnectId = session?.userInfo?.userId ?? "";

  const updatePreferences = useCallback(() => {
    const alertsEmailPreference = !isAlertsEmailsEnabled;
    const eventLabel = alertsEmailPreference
      ? "price_alerts_opt_in"
      : "price_alerts_opt_out";

    trackClickEvent("watchlistAlerts", eventLabel, "click", "watchlist");

    // Toggles email alerts preference (UI only)
    setIsAlertsEmailsEnabled(alertsEmailPreference);

    setIsSuccessToastOpen(false);
    setIsErrorToastOpen(false);

    updateUserPreferences({
      bconnectId,
      alertsEmailsEnabled: alertsEmailPreference,
    })
      .then((response) => {
        const { alertsEmailsEnabled } =
          response.data as UserPreferencesResponse;

        if (alertsEmailsEnabled !== alertsEmailPreference) {
          // Syncs UI with response from backend, this should rarely happen
          setIsAlertsEmailsEnabled(alertsEmailsEnabled);
        }

        setIsSuccessToastOpen(true);
      })
      .catch((error) => {
        setIsErrorToastOpen(true);
      });
  }, [bconnectId, isAlertsEmailsEnabled]);

  const onDropdownButtonClick = useCallback(() => {
    setIsOpen((prevIsOpen) => {
      const eventLabel = prevIsOpen ? "bell_icon_close" : "bell_icon_open";

      trackClickEvent("watchlistAlerts", eventLabel, "click", "watchlist");

      return !prevIsOpen;
    });
  }, []);

  const onHyperlinkClick = useCallback(() => {
    trackClickEvent(
      "watchlistAlerts",
      "learn_more_hyperlink_click",
      "click",
      "watchlist"
    );
  }, []);

  // Dismiss success toast after AUTO_HIDE_DURATION ms
  useEffect(() => {
    if (isSuccessToastOpen) {
      const timer = setTimeout(() => {
        setIsSuccessToastOpen(false);
      }, AUTO_HIDE_DURATION);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isSuccessToastOpen]);

  // Dismiss error toast after AUTO_HIDE_DURATION ms
  useEffect(() => {
    if (isErrorToastOpen) {
      const timer = setTimeout(() => {
        setIsErrorToastOpen(false);
      }, AUTO_HIDE_DURATION);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isErrorToastOpen]);

  useEffect(() => {
    fetchUserPreferences({ bconnectId })
      .then((response) => {
        const fetchedPreferences = response.data as UserPreferencesResponse;

        setIsAlertsEmailsEnabled(fetchedPreferences?.alertsEmailsEnabled);
        setLoadingState(LOADING_STATE.COMPLETE);
      })
      .catch((error) => {
        setLoadingState(LOADING_STATE.FAILED);
      });
  }, [bconnectId]);

  const DropdownMenuOptions = () => {
    if (loadingState === LOADING_STATE.FAILED) {
      return (
        <div className="flex p-4 justify-center items-center">
          Failed to load preferences.
        </div>
      );
    }

    if (loadingState === LOADING_STATE.LOADING) {
      return (
        <div className="flex p-4 justify-center items-center">
          <Spinner />
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-col">
          <p className="pr-1">Significant price change alerts</p>

          <Link
            className="text-sm underline"
            href={WATCHLIST_ALERTS_HELP_PAGE_URL}
            target="_blank"
            onClick={onHyperlinkClick}
          >
            Learn more
          </Link>
        </div>

        <Toggle
          className={classes([
            styles.toggle,
            { [styles.active]: isAlertsEmailsEnabled },
          ])}
          active={isAlertsEmailsEnabled}
          onToggle={updatePreferences}
        />
      </>
    );
  };

  return (
    <div className={styles.wrapper}>
      <button
        className={styles.dropdownButton}
        onClick={onDropdownButtonClick}
        aria-label="manage alerts"
      >
        <NotificationAdd className={styles.bell} />
        <span className={styles.dropdownIcon}>
          {isOpen ? <ChevronUp /> : <ChevronDown />}
        </span>
      </button>

      <DropdownMenu isOpen={isOpen}>
        <DropdownMenuOptions />
      </DropdownMenu>

      <ToastSuccess
        open={isSuccessToastOpen}
        text={isAlertsEmailsEnabled ? OPT_IN_TOAST_MSG : OPT_OUT_TOAST_MSG}
      />
      <ToastError open={isErrorToastOpen} text={ERROR_TOAST_MSG} />
    </div>
  );
}
